import {
  Add,
  CheckCircleOutlineOutlined,
  EditOutlined,
  Refresh,
} from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
} from "@mui/material";
import { FC } from "react";

import { MuiButton } from "@ryde/controls";
import { DATA_E2E_KEY, IncidentStatusEnum } from "@ryde/shared";

import type { TRideIncidentStatusFilter } from "./incident-tab.models";
import {
  RicTypeColorDotStyled,
  RideIncidentsTabWrapStyled,
} from "./ride-incidents-tab.styled";
import { buildRideIncidentClosedMetaLine } from "./ride-incidents-tab.utils";
import { useRideTabIncidents } from "./use-ride-tab-incidents";

export const RideTabsTabIncidents: FC = () => {
  const {
    t,
    rideDto,
    canRead,
    canCreate,
    canUpdate,
    canUpdateStatus,
    statusFilter,
    setStatusFilter,
    loadingList,
    displayRows,
    onEditIncident,
    onCloseIncident,
    formatIncidentCreatedAt,
    openIncidentCreateDrawer,
    refreshIncidents,
  } = useRideTabIncidents();

  if (!rideDto || !canRead) {
    return null;
  }

  return (
    <RideIncidentsTabWrapStyled>
      {canCreate ? (
        <Box className="ric-add-row">
          <MuiButton
            muiVariant="contained"
            muiStyle="warning"
            muiSize="small"
            startIcon={<Add fontSize="small" />}
            onClick={() => void openIncidentCreateDrawer()}
            {...{ [DATA_E2E_KEY]: "ride-incidents-add-incident-button" }}
          >
            {t("ride.incidents.addIncidentLink")}
          </MuiButton>
        </Box>
      ) : null}

      <div className="ric-filter-plaque">
        <FormControl className="ric-filter-plaque__form">
          <RadioGroup
            row
            className="ric-filter-plaque__radio-group"
            aria-label={t("ride.incidents.filterAriaLabel")}
            name="ride-incidents-status-filter"
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value as TRideIncidentStatusFilter);
            }}
          >
            <FormControlLabel
              value="all"
              control={<Radio size="small" color="primary" />}
              label={t("ride.incidents.filterAll")}
            />
            <FormControlLabel
              value={IncidentStatusEnum.New}
              control={<Radio size="small" color="primary" />}
              label={t("ride.incidents.filterNew")}
            />
            <FormControlLabel
              value={IncidentStatusEnum.Closed}
              control={<Radio size="small" color="primary" />}
              label={t("ride.incidents.filterClosed")}
            />
          </RadioGroup>
        </FormControl>

        <IconButton
          type="button"
          size="small"
          color="primary"
          className="ric-filter-plaque__refresh"
          disabled={loadingList}
          aria-label={t("ride.incidents.refreshListAria")}
          title={t("general.REFRESH")}
          onClick={() => void refreshIncidents()}
          {...{ [DATA_E2E_KEY]: "ride-incidents-refresh-list" }}
        >
          <Refresh fontSize="small" />
        </IconButton>
      </div>

      {loadingList ? (
        <p className="ric-loading">{t("general.loading")}</p>
      ) : (
        <ul className="ric-list">
          {displayRows.map(row => {
            const status = row.status;
            const typeName = (
              row.incidentTypeName ??
              row.typeName ??
              ""
            ).trim();
            const when = formatIncidentCreatedAt(row.createdAt);
            const li = row.metadata?.locationInfo;
            const loc = [li?.fullAddress, li?.addressName]
              .filter(Boolean)
              .join(" · ");
            const remarks = (row.metadata?.remarks ?? "").trim();
            const createdByName = (row.userCreatedByName ?? "").trim();
            const typeColor = row.incidentTypeColor?.trim();
            const isClosed = status === IncidentStatusEnum.Closed;
            const closedMetaLine = buildRideIncidentClosedMetaLine({
              row,
              formatIncidentClosedAt: formatIncidentCreatedAt,
              t,
            });

            return (
              <li className="ric-list-item" key={row.id}>
                <div className="ric-row-head">
                  <div className="ric-row-head__left">
                    <span
                      className={`ric-status-chip ${
                        isClosed
                          ? "ric-status-chip--closed"
                          : "ric-status-chip--new"
                      }`}
                    >
                      {isClosed
                        ? t("ride.incidents.statusBadgeClosed")
                        : t("ride.incidents.statusBadgeNew")}
                    </span>
                    <span className="ric-type-block">
                      <p className="ric-row-title">
                        {typeName || `#${row.id}`}
                      </p>
                      {typeColor ? (
                        <RicTypeColorDotStyled
                          $color={typeColor}
                          title={typeName}
                          aria-hidden
                        />
                      ) : null}
                    </span>
                  </div>
                  {createdByName || when ? (
                    <div className="ric-row-head__right">
                      {createdByName ? (
                        <p className="ric-row-head__by">
                          {t("ride.incidents.createdByLine", {
                            name: createdByName,
                          })}
                        </p>
                      ) : null}
                      {when ? (
                        <p className="ric-row-head__when">{when}</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <p className="ric-muted">{loc}</p>
                {closedMetaLine ? (
                  <p className="ric-muted ric-detail ric-detail--closed-meta">
                    {closedMetaLine}
                  </p>
                ) : null}
                {remarks ? (
                  <p className="ric-muted ric-detail ric-detail--remarks">
                    {remarks}
                  </p>
                ) : null}
                <div className="ric-actions">
                  {canUpdate && (
                    <MuiButton
                      muiVariant="outlined"
                      muiSize="small"
                      startIcon={<EditOutlined fontSize="small" />}
                      onClick={() => void onEditIncident(row.id)}
                    >
                      {t("general.EDIT")}
                    </MuiButton>
                  )}
                  {canUpdateStatus && status === IncidentStatusEnum.New && (
                    <MuiButton
                      muiVariant="contained"
                      muiStyle="success"
                      muiSize="small"
                      startIcon={
                        <CheckCircleOutlineOutlined fontSize="small" />
                      }
                      onClick={() => void onCloseIncident(row.id)}
                    >
                      {t("liveMap.incident.closeIncident")}
                    </MuiButton>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </RideIncidentsTabWrapStyled>
  );
};
